import { useState, useCallback } from "react";
import { getCurrentSessionToken, signOutCurrentUser, getCurrentUserEmail } from "../services/cognito";
import { useAuthSession } from "./useAuthSession";
import { useSignIn } from "./useSignIn";

export type AuthStep = "LOGIN" | "NEW_PASSWORD" | "AUTHENTICATED";

export interface UseLoginReturn {
  username: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  error: string;
  isLoading: boolean;
  step: AuthStep;
  userEmail: string | null;
  setUsername: (value: string) => void;
  setPassword: (value: string) => void;
  setNewPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setError: (value: string) => void;
  handleLogin: () => Promise<void>;
  handleNewPassword: () => Promise<void>;
  handleLogout: () => void;
  handleSocialLogin: (provider: "Google" | "HostedUI") => void;
  isInitializing: boolean;
}

export function useLogin(): UseLoginReturn {
  const [step, setStep] = useState<AuthStep>("LOGIN");
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const { isInitializing } = useAuthSession(setStep, setUserEmail, () => {});

  const {
    username, password, newPassword, confirmPassword,
    error, isLoading,
    setUsername, setPassword, setNewPassword, setConfirmPassword, setError,
    handleLogin, handleNewPassword, setPendingUser, setPendingAttributes
  } = useSignIn(setStep, setUserEmail, getCurrentSessionToken, getCurrentUserEmail);

  const handleLogout = useCallback(() => {
    try {
      signOutCurrentUser();
    } catch (e) {
      console.error("SignOut error", e);
    }
    setStep("LOGIN");
    setUsername("");
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setPendingUser(null);
    setPendingAttributes({});
    setUserEmail(null);
  }, [setUsername, setPassword, setNewPassword, setConfirmPassword, setError, setPendingUser, setPendingAttributes]);

  const handleSocialLogin = useCallback((provider: "Google" | "HostedUI") => {
    const domain = process.env.BUN_PUBLIC_COGNITO_DOMAIN;
    const clientId = process.env.BUN_PUBLIC_COGNITO_WEB_CLIENT_ID;
    const redirectUri = process.env.BUN_PUBLIC_COGNITO_REDIRECT_URI;

    if (!domain || !clientId || !redirectUri) {
      setError("Falta configuración de entorno de Cognito (Domain, Client ID, Redirect URI)");
      return;
    }

    let url = `${domain}/oauth2/authorize?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    
    if (provider === "Google") {
      url += "&identity_provider=Google";
    }

    window.location.href = url;
  }, [setError]);

  return {
    username,
    password,
    newPassword,
    confirmPassword,
    error,
    isLoading,
    step,
    userEmail,
    setUsername,
    setPassword,
    setNewPassword,
    setConfirmPassword,
    setError,
    handleLogin,
    handleNewPassword,
    handleLogout,
    handleSocialLogin,
    isInitializing,
  };
}
